import React, { useMemo } from 'react'
import { Chart } from 'react-google-charts'

export interface PendingRefundMonthRow {
  monthKey: string
  monthLabel: string
  bailiff_pending: number
  bidder_pending: number
}

export interface PendingRefundBarChartProps {
  rows: PendingRefundMonthRow[]
  /** Total chart height in px (header is outside this) */
  chartHeight?: number
}

function pendingCount(value: unknown): number {
  const n = Number(value)
  if (!Number.isFinite(n)) {
    return 0
  }
  return n < 0 ? 0 : n
}

/**
 * Grouped column chart: PENDING bailiff vs PENDING bidder refund requests per month (created_at).
 */
const PendingRefundBarChart: React.FC<PendingRefundBarChartProps> = ({
  rows,
  chartHeight = 260
}) => {
  const safeRows = rows ?? []

  const chartData = useMemo(() => {
    const header: Array<string | number> = [
      'Month',
      'Bailiff refund (PENDING)',
      'Lost bidders (PENDING)'
    ]
    const body = safeRows.map((r) => [
      String(r.monthLabel ?? ''),
      pendingCount(r.bailiff_pending),
      pendingCount(r.bidder_pending)
    ])
    if (body.length === 0) {
      return null
    }
    return [header, ...body]
  }, [safeRows])

  const options = useMemo(
    () => ({
      legend: {
        position: 'bottom',
        alignment: 'center',
        textStyle: { fontSize: 11, color: '#475569' }
      },
      chartArea: { width: '90%', height: '58%', top: 8, bottom: 52 },
      colors: ['#0f766e', '#4338ca'],
      backgroundColor: 'transparent',
      hAxis: {
        textStyle: { color: '#64748b', fontSize: 10 },
        slantedText: true,
        slantedTextAngle: 42
      },
      vAxis: {
        minValue: 0,
        format: '0',
        baselineColor: '#e2e8f0',
        gridlines: { color: '#f1f5f9' },
        textStyle: { color: '#64748b', fontSize: 11 }
      },
      bar: { groupWidth: '72%' },
      animation: { startup: true, duration: 400, easing: 'out' }
    }),
    []
  )

  return (
    <div style={{ marginTop: 12 }}>
      <div
        style={{
          background: '#dff0de',
          color: '#000000',
          padding: '10px 16px',
          borderRadius: '10px',
          fontWeight: 'normal',
          fontSize: '15px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          marginBottom: '16px'
        }}
      >
        <span style={{ fontSize: '18px', lineHeight: 1 }} aria-hidden>
          📊
        </span>
        <span>
          {'PENDING refund requests by month (created_at) - bailiff & lost bidders'}
        </span>
      </div>
      <div style={{ width: '100%', maxWidth: '100%', minHeight: chartHeight }}>
        {chartData === null
          ? (
              <div
                role="status"
                aria-label="No chart data"
                style={{
                  height: `${chartHeight}px`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#64748b',
                  fontSize: 14,
                  background: 'transparent',
                  borderRadius: 8
                }}
              >
                No data
              </div>
            )
          : (
              <Chart
                chartType="ColumnChart"
                width="100%"
                height={`${chartHeight}px`}
                data={chartData}
                options={options}
              />
            )}
      </div>
    </div>
  )
}

export default PendingRefundBarChart
